import React, { useState, useEffect, useRef } from 'react';
import Select from '../../../components/ui/Select';
import { Toggle } from '../../../components/ui/Toggle';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TravelPreferencesSection = ({ travelPreferences, onUpdatePreferences }) => {
  const [preferences, setPreferences] = useState({
    preferredParkTypes: travelPreferences?.preferredParkTypes || [],
    activityInterests: travelPreferences?.activityInterests || [],
    accommodationStyle: travelPreferences?.accommodationStyle || '',
    groupSize: travelPreferences?.groupSize || '',
    budgetRange: travelPreferences?.budgetRange || '',
    travelSeason: travelPreferences?.travelSeason || [],
    difficultyLevel: travelPreferences?.difficultyLevel || '',
    accessibilityNeeds: travelPreferences?.accessibilityNeeds || []
  });

  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Update local state when travelPreferences prop changes
  useEffect(() => {
    if (travelPreferences) {
      setPreferences({
        preferredParkTypes: travelPreferences?.preferredParkTypes || [],
        activityInterests: travelPreferences?.activityInterests || [],
        accommodationStyle: travelPreferences?.accommodationStyle || '',
        groupSize: travelPreferences?.groupSize || '',
        budgetRange: travelPreferences?.budgetRange || '',
        travelSeason: travelPreferences?.travelSeason || [],
        difficultyLevel: travelPreferences?.difficultyLevel || '',
        accessibilityNeeds: travelPreferences?.accessibilityNeeds || []
      });
    }
  }, [travelPreferences]);

  // Auto-save function with debounce
  const autoSavePreferences = async (updatedPreferences) => {
    // Clear any existing timeout
    if (saveTimeoutRef?.current) {
      clearTimeout(saveTimeoutRef?.current);
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        await onUpdatePreferences(updatedPreferences);
      } catch (error) {
        console.error('Failed to auto-save travel preferences:', error);
      } finally {
        setIsSaving(false);
      }
    }, 500); // 500ms debounce
  };

  const parkTypeOptions = [
    { value: 'national', label: 'National Parks' },
    { value: 'state', label: 'State Parks' },
    { value: 'regional', label: 'Regional Parks' },
    { value: 'wilderness', label: 'Wilderness Areas' },
    { value: 'recreation', label: 'Recreation Areas' },
    { value: 'historic', label: 'Historic Sites' }
  ];

  const activityOptions = [
    { value: 'hiking', label: 'Hiking & Trails' },
    { value: 'camping', label: 'Camping' },
    { value: 'photography', label: 'Photography' },
    { value: 'wildlife', label: 'Wildlife Watching' },
    { value: 'fishing', label: 'Fishing' },
    { value: 'boating', label: 'Boating & Water Sports' },
    { value: 'climbing', label: 'Rock Climbing' },
    { value: 'cycling', label: 'Cycling & Biking' },
    { value: 'stargazing', label: 'Stargazing' },
    { value: 'picnicking', label: 'Picnicking' }
  ];

  const accommodationOptions = [
    { value: 'tent-camping', label: 'Tent Camping' },
    { value: 'rv-camping', label: 'RV Camping' },
    { value: 'cabin-rental', label: 'Cabin Rental' },
    { value: 'lodge', label: 'Park Lodge' },
    { value: 'day-trips', label: 'Day Trips Only' },
    { value: 'backcountry', label: 'Backcountry Camping' }
  ];

  const groupSizeOptions = [
    { value: 'solo', label: 'Solo Travel' },
    { value: 'couple', label: 'Couple (2 people)' },
    { value: 'small-family', label: 'Small Family (3-4 people)' },
    { value: 'large-family', label: 'Large Family (5+ people)' },
    { value: 'small-group', label: 'Small Group (5-10 people)' },
    { value: 'large-group', label: 'Large Group (10+ people)' }
  ];

  const budgetOptions = [
    { value: 'budget', label: 'Budget ($0-$100/day)' },
    { value: 'moderate', label: 'Moderate ($100-$250/day)' },
    { value: 'comfortable', label: 'Comfortable ($250-$500/day)' },
    { value: 'luxury', label: 'Luxury ($500+/day)' }
  ];

  const seasonOptions = [
    { value: 'spring', label: 'Spring (Mar-May)' },
    { value: 'summer', label: 'Summer (Jun-Aug)' },
    { value: 'fall', label: 'Fall (Sep-Nov)' },
    { value: 'winter', label: 'Winter (Dec-Feb)' }
  ];

  const difficultyOptions = [
    { value: 'easy', label: 'Easy - Minimal physical activity' },
    { value: 'moderate', label: 'Moderate - Some hiking and activity' },
    { value: 'challenging', label: 'Challenging - Strenuous activities' },
    { value: 'expert', label: 'Expert - Advanced outdoor skills required' }
  ];

  const accessibilityOptions = [
    { value: 'wheelchair', label: 'Wheelchair Accessible Trails' },
    { value: 'mobility', label: 'Limited Mobility Accommodations' },
    { value: 'visual', label: 'Visual Impairment Support' },
    { value: 'hearing', label: 'Hearing Impairment Support' },
    { value: 'service-animals', label: 'Service Animal Friendly' }
  ];

  const handleMultiSelectChange = (field, value, checked) => {
    const updatedPreferences = {
      ...preferences,
      [field]: checked 
        ? [...(preferences?.[field] || []), value]
        : (preferences?.[field] || [])?.filter(item => item !== value)
    };
    
    setPreferences(updatedPreferences);
    autoSavePreferences(updatedPreferences);
  };

  const handleSingleSelectChange = (field, value) => {
    const updatedPreferences = {
      ...preferences,
      [field]: value
    };
    
    setPreferences(updatedPreferences);
    autoSavePreferences(updatedPreferences);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef?.current) {
        clearTimeout(saveTimeoutRef?.current);
      }
    };
  }, []);

  const PreferenceGroup = ({ title, icon, iconColor, children }) => (
    <div className="bg-card rounded-organic border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className={`w-10 h-10 ${iconColor} rounded-full flex items-center justify-center`}>
          <Icon name={icon} size={20} color="white" />
        </div>
        <div>
          <h3 className="font-heading font-heading-bold text-lg text-card-foreground">{title}</h3>
        </div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header without Save Button */}
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-organic border border-border">
        <div>
          <h3 className="font-body font-body-medium text-sm text-card-foreground">Travel Preferences</h3>
          <p className="font-caption text-xs text-muted-foreground mt-1">
            {isSaving ? 'Saving...' : 'Your preferences are saved automatically'}
          </p>
        </div>
      </div>

      {/* Park Types & Activities */}
      <PreferenceGroup title="Park Types & Activities" icon="MapPin" iconColor="bg-primary">
        <div className="space-y-6">
          <div>
            <h4 className="font-body font-body-medium text-sm text-card-foreground mb-3">Preferred Park Types</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {parkTypeOptions?.map((option) => (
                <Toggle
                  key={option?.value}
                  id={`park-type-${option?.value}`}
                  checked={preferences?.preferredParkTypes?.includes(option?.value)}
                  onChange={(e) => handleMultiSelectChange('preferredParkTypes', option?.value, e?.target?.checked)}
                  label={option?.label}
                />
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-body font-body-medium text-sm text-card-foreground mb-3">Activity Interests</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activityOptions?.map((option) => (
                <Toggle
                  key={option?.value}
                  id={`activity-${option?.value}`}
                  checked={preferences?.activityInterests?.includes(option?.value)}
                  onChange={(e) => handleMultiSelectChange('activityInterests', option?.value, e?.target?.checked)}
                  label={option?.label}
                />
              ))}
            </div>
          </div>
        </div>
      </PreferenceGroup>
      {/* Travel Style */}
      <PreferenceGroup title="Travel Style" icon="Users" iconColor="bg-accent">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Accommodation Style"
            options={accommodationOptions}
            value={preferences?.accommodationStyle}
            onChange={(value) => handleSingleSelectChange('accommodationStyle', value)}
            placeholder="Select accommodation preference"
          />

          <Select
            label="Typical Group Size"
            options={groupSizeOptions}
            value={preferences?.groupSize}
            onChange={(value) => handleSingleSelectChange('groupSize', value)}
            placeholder="Select group size"
          />

          <Select
            label="Budget Range"
            options={budgetOptions}
            value={preferences?.budgetRange}
            onChange={(value) => handleSingleSelectChange('budgetRange', value)}
            placeholder="Select budget range"
          />

          <Select
            label="Difficulty Level"
            options={difficultyOptions}
            value={preferences?.difficultyLevel}
            onChange={(value) => handleSingleSelectChange('difficultyLevel', value)}
            placeholder="Select difficulty preference"
          />
        </div>
      </PreferenceGroup>
      {/* Seasonal Preferences */}
      <PreferenceGroup title="Seasonal Preferences" icon="Calendar" iconColor="bg-secondary">
        <div>
          <h4 className="font-body font-body-medium text-sm text-card-foreground mb-3">Preferred Travel Seasons</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {seasonOptions?.map((option) => (
              <Toggle
                key={option?.value}
                id={`season-${option?.value}`}
                checked={preferences?.travelSeason?.includes(option?.value)}
                onChange={(e) => handleMultiSelectChange('travelSeason', option?.value, e?.target?.checked)}
                label={option?.label}
              />
            ))}
          </div>
        </div>
      </PreferenceGroup>
      {/* Accessibility */}
      <PreferenceGroup title="Accessibility Needs" icon="Heart" iconColor="bg-success">
        <div>
          <h4 className="font-body font-body-medium text-sm text-card-foreground mb-3">Accessibility Requirements</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {accessibilityOptions?.map((option) => (
              <Toggle
                key={option?.value}
                id={`accessibility-${option?.value}`}
                checked={preferences?.accessibilityNeeds?.includes(option?.value)}
                onChange={(e) => handleMultiSelectChange('accessibilityNeeds', option?.value, e?.target?.checked)}
                label={option?.label}
              />
            ))}
          </div>
        </div>
      </PreferenceGroup>
    </div>
  );
};

export default TravelPreferencesSection;